/**
 * Technology Domain Edit Route
 *
 * Edit an existing technology domain using TechnologyDomainForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTechnologyDomain } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { TechnologyDomainForm } from '../../components/technology-domains';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import {
  updateTechnologyDomain,
  type UpdateTechnologyDomainInput,
  ApiError,
} from '../../lib/apiClient';
import type { CreateTechnologyDomainFormData } from '../../components/forms';
import './edit.css';

export const Route = createFileRoute('/technology-domains/$technologyDomainId/edit')({
  component: TechnologyDomainEditPage,
});

function TechnologyDomainEditPage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const { technologyDomainId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch technology domain data
  const { data, loading, error: fetchError } = useTechnologyDomain(technologyDomainId);
  const technologyDomain = data?.technologyDomain;

  if (loading) {
    return (
      <div className="technology-domain-edit-page">
        <LoadingSpinner centered message="Loading technology domain..." />
      </div>
    );
  }

  if (fetchError || !technologyDomain) {
    return (
      <div className="technology-domain-edit-page">
        <EmptyState
          icon="error"
          title="Failed to load technology domain"
          description={fetchError?.message || 'Technology domain not found'}
          actionLabel="Back to Technology Domains"
          onAction={() =>
            navigate({ to: '/technology-domains', search: { page: 1, pageSize: 25 } })
          }
        />
      </div>
    );
  }

  const handleSubmit = async (formData: CreateTechnologyDomainFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build update input
      const updates: UpdateTechnologyDomainInput = {
        name: formData.name,
        description: formData.description || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
        notes: formData.notes || undefined,
      };

      await updateTechnologyDomain(technologyDomainId, updates);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['GetTechnologyDomain', 'ListTechnologyDomains'],
      });

      // Navigate back to technology domain detail page
      navigate({ to: `/technology-domains/${technologyDomainId}` as string });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: `/technology-domains/${technologyDomainId}` as string });
  };

  // Map technology domain data to form default values
  const defaultValues: Partial<CreateTechnologyDomainFormData> = {
    name: technologyDomain.name,
    description: technologyDomain.description || undefined,
    tags: technologyDomain.tags || [],
    notes: technologyDomain.notes || undefined,
  };

  return (
    <div className="technology-domain-edit-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Technology Domain</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Update technology domain information
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <TechnologyDomainForm
        mode="edit"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
